'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Map as ImmutableMap } from 'immutable';
import { createLocation } from 'history';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import usePrevious from 'react-utils/hooks/usePrevious';
import { LayoutWithCollaborationSidebar, FEATURES, SUPPORTED_HUBS } from 'collaboration-sidebar';
import FeedbackLoader from 'feedback-loader';
import OnboardingTour from 'onboarding-tours/components/OnboardingTour';
import NotificationList from 'ReduxMessenger/components/NotificationList';
import SocialComposerPanel from 'social-composer-lib/components/SocialComposerPanel';
import UIUpgradeBanner from 'ui-addon-upgrades/ums/UIUpgradeBanner';
import UIDashboardPage from 'UIComponents/page/UIDashboardPage';
import { SuspensionBannersContextProvider, ContextSuspensionBanners } from 'ui-suspension-banners-lib';
import ContentUsageLimitUpgradeBanner from '../components/ContentUsageLimitUpgradeBanner';
import ErrorPage from '../components/ErrorPage';
import OptimizationSidebar from '../components/OptimizationSidebar';
import TriggerWootricSurvey from '../components/TriggerWootricSurvey';
import { parse, stringify } from 'hub-http/helpers/params';
import setDocumentTitle from 'I18n/utils/setDocumentTitle';
import { PENDING, SUCCEEDED } from 'reporting-data/redux/StatusTypes';
import { navigatedToExternalPage as _navigatedToExternalPage, navigatedToPage as _navigatedToPage } from '../actions/ApplicationActions';
import { closeSocialComposer as _closeSocialComposer } from '../actions/ModalActions';
import { CONTENT_TYPE_PROPS, ContentTypes, PATHS, PATHS_PROPS, REQUEST_STATUS_PROPS, RequestStatus, ContentTypeToCommentObjectType, LANDING_PAGES_TRUSTED_SCOPE, ACCOUNT_VERIFICATION_SOURCE } from '../constants/base';
import { PATAGONIA_EDITOR_REDESIGN_OPTIN_NAME } from '../constants/optins';
import useGetOptinStatus from '../hooks/useGetOptInStatus';
import { getUserHasVideo, getUserHasGoogleSearchConsoleAccess, getUserHasReadAccess } from '../selectors/authSelectors';
import { getContentAbsoluteUrl, getContentHasTranslatedContent, getContentRequestErrorStatusCode, getContentRequestErrorType, getContentIsVariant, getContentRequestStatus } from '../selectors/contentSelectors';
import { getShouldShowFreeBlogRemoveBrandingBanner, getShouldShowFreeLandingPagesRemoveBrandingBanner, getShouldShowFreeSitePagesRemoveBrandingBanner } from '../selectors/contentUpgradeSelectors';
import { getHistoryCreateHref, getHistoryPush } from '../selectors/historySelectors';
import { getLocationPathname, getLocationRoute, getMatchDefaultPath, getLocationSection } from '../selectors/locationSelectors';
import { getHasMAB, getMABMasterId, getMABStatus } from '../selectors/mabSelectors';
import { getMatchContentPageId, getMatchContentSlug, getMatchContentSlugType, getMatchCrmObjectTypeId, getMatchHubDbTableId, getMatchPageId, getMatchIsInternalContent } from '../selectors/matchSelectors';
import { getSocialComopserOpen, getSocialComposerParams, getAdsPanelOpen } from '../selectors/modalsDisplaySelectors';
import { getVariationsHasVariations } from '../selectors/variationsSelectors';
import { getHeaderParams } from '../utils/headerParams';
import { makeAsyncComponent } from '../utils/makeAsyncUtils';
import { usageTracker } from '../utils/usageTracker';
const AsyncHeader = makeAsyncComponent(() => import(
/* webpackChunkName: "header", webpackPreload: true */
'./Header'), {
  maxRetries: 2
});
const AsyncPerformance = makeAsyncComponent(() => import(
/* webpackChunkName: "performance", webpackPrefetch: true */
'./Performance'), {
  maxRetries: 2
});
const AsyncOptimization = makeAsyncComponent(() => import(
/* webpackChunkName: "optimization" */
'./Optimization'), {
  maxRetries: 2
});
const AsyncSmartContentReport = makeAsyncComponent(() => import(
/* webpackChunkName: "smart-content" */
'./SmartContent'), {
  maxRetries: 2
});
const AsyncTestResultsLoader = makeAsyncComponent(() => import(
/* webpackChunkName: "test-results" */
'./TestResultsLoader'), {
  maxRetries: 2
});
const AsyncAdsPanel = makeAsyncComponent(() => import(
/* webpackChunkName: "ads-panel" */
'./AdsPanel'), {
  maxRetries: 2
});

const Application = ({
  absoluteUrl,
  adsPanelOpen,
  closeSocialComposer,
  contentPageId,
  contentRequestErrorStatusCode,
  contentRequestErrorType,
  contentRequestStatus,
  contentSlug,
  contentType,
  createHref,
  crmObjectTypeId,
  currentSection,
  defaultPath,
  hasContentAccess,
  hasMab,
  // eslint-disable-next-line no-unused-vars
  hasTranslatedContent,
  // eslint-disable-next-line no-unused-vars
  hasVariations,
  hasVideo,
  hubDbTableId,
  isInternal,
  isVariant,
  location,
  mabStatus,
  masterMabId,
  navigatedToExternalPage,
  navigatedToPage,
  pageId,
  pathname,
  push,
  // eslint-disable-next-line no-unused-vars
  route,
  shouldShowFreeBlogRemoveBrandingBanner,
  shouldShowFreeLandingPagesRemoveBrandingBanner,
  shouldShowFreeSitePagesRemoveBrandingBanner,
  socialComposerOpen,
  socialComposerParams,
  userHasGoogleSearchConsoleAccess
}) => {
  const previousMabStatus = usePrevious(mabStatus);
  const {
    isOptedIntoBeta: isOptedIntoPatagoniaBeta
  } = useGetOptinStatus(PATAGONIA_EDITOR_REDESIGN_OPTIN_NAME);
  useEffect(() => {
    setDocumentTitle('app.htmlTitle');
  }, []);
  useEffect(() => {
    if (hasContentAccess) {
      AsyncHeader.preload();
    }
  }, [hasContentAccess]);
  useEffect(() => {
    if (hasContentAccess) {
      if (contentType === ContentTypes.EXTERNAL_PAGE) {
        const params = parse(location.search.slice(1));
        navigatedToExternalPage(Object.assign({
          id: pageId
        }, params));
      } else {
        navigatedToPage({
          contentPageId,
          contentType,
          crmObjectTypeId,
          hasVideo,
          hubDbTableId
        });
      }
    }
  }, [contentPageId, contentType, crmObjectTypeId, hasContentAccess, hasVideo, hubDbTableId, location.search, navigatedToExternalPage, navigatedToPage, pageId]);
  useEffect(() => {
    if (previousMabStatus === PENDING && mabStatus === SUCCEEDED) {
      if (isVariant && hasMab && masterMabId) {
        const currentQuery = parse(location.search ? location.search.replace('?', '') : '');
        const search = stringify(Object.assign({}, currentQuery, {
          variation: contentPageId
        })) || '';
        push({
          pathname: `/${contentSlug}/${masterMabId}/${currentSection}`,
          search
        });
      }
    }
  }, [contentPageId, contentSlug, currentSection, hasMab, isVariant, location.search, mabStatus, masterMabId, previousMabStatus, push]);

  const getDefaultTrackingParams = () => {
    return {
      'page-type': contentType,
      screen: 'detail',
      subscreen: currentSection
    };
  };

  const handleSidebarCreateHref = sidebar => {
    const _pathname = contentSlug === ContentTypes.EXTERNAL_PAGE ? `/${contentSlug}/optimization/${sidebar}` : `/${contentSlug}/${pageId}/optimization/${sidebar}`;

    return createHref(createLocation({
      pathname: _pathname,
      search: location.search
    }, null, null, location));
  };

  const handleTabCreateHref = tab => {
    return createHref(createLocation({
      pathname: `/${contentSlug}/${pageId}/${tab}`,
      search: location.search
    }, null, null, location));
  };

  const handleTabOnClick = tab => {
    usageTracker.track('exploreReport', Object.assign({}, getDefaultTrackingParams(), {
      action: 'clicked-tab',
      tab
    }));
    push({
      pathname: `/${contentSlug}/${pageId}/${tab}`,
      search: location.search
    });
  };

  const handlePageCreateHref = (_pageId, tab = currentSection) => {
    return createHref(createLocation({
      pathname: `/${contentSlug}/${_pageId}/${tab}`,
      search: location.search
    }, null, null, location));
  };

  const handlePageChange = (_pageId, tab = currentSection, search = '') => {
    push({
      pathname: `/${contentSlug}/${_pageId}/${tab}`,
      search
    });
  };

  const renderHeader = () => {
    return /*#__PURE__*/_jsx(AsyncHeader, {
      createTabHref: handleTabCreateHref,
      isOptedIntoPatagoniaBeta: isOptedIntoPatagoniaBeta,
      onPageChange: handlePageChange,
      onPageCreateHref: handlePageCreateHref,
      onTabClick: handleTabOnClick
    });
  };

  const renderSidebar = () => {
    if (currentSection !== PATHS.OPTIMIZATION) {
      return null;
    }

    return /*#__PURE__*/_jsx(OptimizationSidebar, {
      createHref: handleSidebarCreateHref,
      isInternal: isInternal,
      onClick: push,
      pathname: pathname,
      userHasGoogleSearchConsoleAccess: userHasGoogleSearchConsoleAccess
    });
  };

  const renderSocialComposer = () => {
    if (!socialComposerOpen) {
      return null;
    }

    return /*#__PURE__*/_jsx(SocialComposerPanel, Object.assign({
      clientTag: "content-detail-ui'",
      onCloseModal: closeSocialComposer,
      open: socialComposerOpen
    }, socialComposerParams.toJS()));
  };

  const renderAdsPanel = () => {
    if (!adsPanelOpen) {
      return null;
    }

    return /*#__PURE__*/_jsx(AsyncAdsPanel, {});
  };

  const renderPage = () => {
    return /*#__PURE__*/_jsxs(UIDashboardPage, {
      headerComponent: renderHeader(),
      sidebarComponent: renderSidebar(),
      children: [/*#__PURE__*/_jsxs(Switch, {
        children: [/*#__PURE__*/_jsx(Route, {
          component: AsyncPerformance,
          path: `/:contentSlug/:pageId/${PATHS.PERFORMANCE}`
        }), /*#__PURE__*/_jsx(Route, {
          component: AsyncPerformance,
          path: `/:contentSlug/:pageId/${PATHS.PUBLISHED}`
        }), /*#__PURE__*/_jsx(Redirect, {
          from: `/:contentSlug/:pageId/${PATHS.SCHEDULED}`,
          to: `/:contentSlug/:pageId/${PATHS.PUBLISHED}`
        }), /*#__PURE__*/_jsx(Route, {
          component: AsyncOptimization,
          path: `/:contentSlug/:pageId?/${PATHS.OPTIMIZATION}`
        }), /*#__PURE__*/_jsx(Route, {
          component: AsyncSmartContentReport,
          path: `/:contentSlug/:pageId/${PATHS.SMART_CONTENT}`
        }), /*#__PURE__*/_jsx(Route, {
          component: AsyncTestResultsLoader,
          path: `/:contentSlug/:pageId/${PATHS.TEST_RESULTS}`
        }), /*#__PURE__*/_jsx(Redirect, {
          to: `/${contentSlug}/${pageId}/${defaultPath}`
        })]
      }), /*#__PURE__*/_jsx(NotificationList, {}), renderAdsPanel(), renderSocialComposer(), /*#__PURE__*/_jsx(FeedbackLoader, {})]
    });
  };

  if (!hasContentAccess || contentRequestStatus === RequestStatus.FAILED && !absoluteUrl) {
    const statusCode = !hasContentAccess ? 403 : contentRequestErrorStatusCode;
    return /*#__PURE__*/_jsx(ErrorPage, {
      contentType: contentType,
      errorType: contentRequestErrorType,
      statusCode: statusCode
    });
  }

  const isWebsitePage = contentType === ContentTypes.SITE_PAGE;
  const appHub = isWebsitePage ? SUPPORTED_HUBS.CMS_HUB : [SUPPORTED_HUBS.CMS_HUB, SUPPORTED_HUBS.MARKETING_HUB];
  return /*#__PURE__*/_jsx(LayoutWithCollaborationSidebar, {
    appHub: appHub,
    appName: "content-detail",
    enabledFeatures: [FEATURES.COMMENTS, FEATURES.TASKS, FEATURES.CALENDAR],
    objectId: contentPageId,
    objectType: ContentTypeToCommentObjectType[contentType],
    scrollablePage: true,
    children: /*#__PURE__*/_jsxs(SuspensionBannersContextProvider, {
      children: [/*#__PURE__*/_jsx(ContextSuspensionBanners, {
        scope: LANDING_PAGES_TRUSTED_SCOPE,
        source: ACCOUNT_VERIFICATION_SOURCE
      }), /*#__PURE__*/_jsx(OnboardingTour, {}), shouldShowFreeLandingPagesRemoveBrandingBanner && /*#__PURE__*/_jsx(UIUpgradeBanner, {
        className: "m-x-5 m-top-5",
        upgradeKey: "landing-pages-details-page-remove-branding"
      }), shouldShowFreeSitePagesRemoveBrandingBanner && /*#__PURE__*/_jsx(UIUpgradeBanner, {
        className: "m-x-5 m-top-5",
        upgradeKey: "site-pages-details-page-remove-branding"
      }), shouldShowFreeBlogRemoveBrandingBanner && /*#__PURE__*/_jsx(UIUpgradeBanner, {
        className: "m-x-5 m-top-5",
        upgradeKey: "blog-posts-details-page-remove-branding"
      }), /*#__PURE__*/_jsx(ContentUsageLimitUpgradeBanner, {
        contentType: contentType
      }), renderPage(), /*#__PURE__*/_jsx(TriggerWootricSurvey, {
        enabled: true
      })]
    })
  });
};

Application.propTypes = {
  absoluteUrl: PropTypes.string,
  adsPanelOpen: PropTypes.bool.isRequired,
  closeSocialComposer: PropTypes.func.isRequired,
  contentPageId: PropTypes.string,
  contentRequestErrorStatusCode: PropTypes.number,
  contentRequestErrorType: PropTypes.string,
  contentRequestStatus: PropTypes.oneOf(REQUEST_STATUS_PROPS),
  contentSlug: PropTypes.string.isRequired,
  contentType: PropTypes.oneOf(CONTENT_TYPE_PROPS).isRequired,
  createHref: PropTypes.func.isRequired,
  crmObjectTypeId: PropTypes.string,
  currentSection: PropTypes.oneOf(PATHS_PROPS).isRequired,
  defaultPath: PropTypes.oneOf(PATHS_PROPS).isRequired,
  hasContentAccess: PropTypes.bool.isRequired,
  hasMab: PropTypes.bool.isRequired,
  hasTranslatedContent: PropTypes.bool.isRequired,
  hasVariations: PropTypes.bool.isRequired,
  hasVideo: PropTypes.bool.isRequired,
  hubDbTableId: PropTypes.string,
  isInternal: PropTypes.bool,
  isVariant: PropTypes.bool,
  location: PropTypes.object.isRequired,
  mabStatus: PropTypes.oneOf(REQUEST_STATUS_PROPS),
  masterMabId: PropTypes.string,
  navigatedToExternalPage: PropTypes.func.isRequired,
  navigatedToPage: PropTypes.func.isRequired,
  pageId: PropTypes.string,
  pathname: PropTypes.string.isRequired,
  push: PropTypes.func.isRequired,
  route: PropTypes.shape({
    path: PropTypes.string
  }),
  shouldShowFreeBlogRemoveBrandingBanner: PropTypes.bool.isRequired,
  shouldShowFreeLandingPagesRemoveBrandingBanner: PropTypes.bool.isRequired,
  shouldShowFreeSitePagesRemoveBrandingBanner: PropTypes.bool.isRequired,
  socialComposerOpen: PropTypes.bool.isRequired,
  socialComposerParams: PropTypes.instanceOf(ImmutableMap),
  userHasGoogleSearchConsoleAccess: PropTypes.bool.isRequired
};

const mapStateToProps = (state, props) => Object.assign({}, getHeaderParams(props), {
  absoluteUrl: getContentAbsoluteUrl(state, props),
  adsPanelOpen: getAdsPanelOpen(state),
  contentPageId: getMatchContentPageId(state, props),
  contentRequestErrorStatusCode: getContentRequestErrorStatusCode(state),
  contentRequestErrorType: getContentRequestErrorType(state),
  contentRequestStatus: getContentRequestStatus(state),
  contentSlug: getMatchContentSlug(state, props),
  contentType: getMatchContentSlugType(state, props),
  createHref: getHistoryCreateHref(state, props),
  crmObjectTypeId: getMatchCrmObjectTypeId(state, props),
  currentSection: getLocationSection(state, props),
  defaultPath: getMatchDefaultPath(state, props),
  hasContentAccess: getUserHasReadAccess(state, props),
  hasMab: getHasMAB(state),
  hasTranslatedContent: getContentHasTranslatedContent(state),
  hasVariations: getVariationsHasVariations(state),
  hasVideo: getUserHasVideo(state),
  hubDbTableId: getMatchHubDbTableId(state, props),
  isInternal: getMatchIsInternalContent(state, props),
  isVariant: getContentIsVariant(state),
  mabStatus: getMABStatus(state),
  masterMabId: getMABMasterId(state),
  pageId: getMatchPageId(state, props),
  pathname: getLocationPathname(state, props),
  push: getHistoryPush(state, props),
  route: getLocationRoute(state, props),
  shouldShowFreeBlogRemoveBrandingBanner: getShouldShowFreeBlogRemoveBrandingBanner(state, props),
  shouldShowFreeLandingPagesRemoveBrandingBanner: getShouldShowFreeLandingPagesRemoveBrandingBanner(state, props),
  shouldShowFreeSitePagesRemoveBrandingBanner: getShouldShowFreeSitePagesRemoveBrandingBanner(state, props),
  socialComposerOpen: getSocialComopserOpen(state),
  socialComposerParams: getSocialComposerParams(state),
  userHasGoogleSearchConsoleAccess: getUserHasGoogleSearchConsoleAccess(state)
});

const mapDispatchToProps = {
  closeSocialComposer: _closeSocialComposer,
  navigatedToExternalPage: _navigatedToExternalPage,
  navigatedToPage: _navigatedToPage
};
const connectedComponent = connect(mapStateToProps, mapDispatchToProps)(Application);
export default connectedComponent;